import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { SubRoutePartnersLayoutService } from '../sub-route-partners-layout.service';
import { BPGroupingCriteria } from '../types/bp-grouping.types';

export type ViewMode = 'list' | 'group';

/**
 * Service for the assigned partners panel.
 * Manages viewMode, grouping criteria, and search term.
 * Child services (list, grouped) consume data from here.
 */
@Injectable()
export class AssignedPartnersPanelService {
  private layoutService = inject(SubRoutePartnersLayoutService);

  // === STATE SIGNALS ===
  private _viewMode = signal<ViewMode>('list');
  private _groupingCriteria = signal<BPGroupingCriteria>(BPGroupingCriteria.NONE);

  /** Search term shared between list and grouped views */
  readonly searchTerm = signal<string>('');

  // === ACTION SUBJECTS ===
  readonly setViewMode$ = new Subject<ViewMode>();
  readonly setGroupingCriteria$ = new Subject<BPGroupingCriteria>();

  // === PUBLIC COMPUTED ===
  readonly viewMode = computed(() => this._viewMode());
  readonly groupingCriteria = computed(() => this._groupingCriteria());

  // Expose layout data
  readonly subRouteData = this.layoutService.subRouteData;

  // === COMPUTED FOR SPECIFIC VIEWS ===
  /** Emits subRouteId only when in list mode */
  readonly subRouteIdForListView = computed(() => {
    const id = this.layoutService.subRouteId();
    const mode = this._viewMode();
    return mode === 'list' ? id : null;
  });

  /** Emits subRouteId only when in group mode */
  readonly subRouteIdForGroupView = computed(() => {
    const id = this.layoutService.subRouteId();
    const mode = this._viewMode();
    return mode === 'group' ? id : null;
  });

  constructor() {
    // Handle viewMode changes
    this.setViewMode$.pipe(takeUntilDestroyed()).subscribe((mode) => {
      this._viewMode.set(mode);
    });

    // Handle grouping criteria changes - also updates viewMode
    this.setGroupingCriteria$.pipe(takeUntilDestroyed()).subscribe((criteria) => {
      this._groupingCriteria.set(criteria);
      // Update viewMode based on criteria
      this._viewMode.set(criteria === BPGroupingCriteria.NONE ? 'list' : 'group');
    });
  }
}
